// Tooltip — the a11y replacement for bare `title=` (DESIGN-SYSTEM.md §4/§6).
// Built on @floating-ui/react: opens on hover (with delay) AND keyboard focus,
// closes on Esc / blur / mouse-leave, portals to <body>, and wires
// `aria-describedby` from the trigger to the tooltip so screen readers announce it.
//
//   <Tooltip content="Resize the panel"><IconButton …/></Tooltip>
//
// The single child is cloned with ref + trigger props, so it must forward a ref
// and spread props (all kit controls do). `content` is a string or node.

import {
  cloneElement,
  isValidElement,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  type Placement,
} from "@floating-ui/react";

export interface TooltipProps {
  readonly content: ReactNode;
  readonly placement?: Placement;
  /** Open delay in ms (default 400). Close is immediate. */
  readonly delay?: number;
  readonly children: ReactElement;
}

export function Tooltip({
  content,
  placement = "top",
  delay = 400,
  children,
}: TooltipProps) {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  const hover = useHover(context, { move: false, delay: { open: delay, close: 0 } });
  const focus = useFocus(context);
  const dismiss = useDismiss(context, { escapeKey: true });
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  // `children` is typed ReactElement; read its own props (incl. any ref) so we
  // can compose them. React 19 exposes a child ref as a normal prop; older
  // React as `element.ref` — cover both.
  const childProps = (isValidElement(children) ? children.props : {}) as Record<
    string,
    unknown
  >;
  const childRef =
    (childProps.ref as React.Ref<unknown> | undefined) ??
    (children as { ref?: React.Ref<unknown> }).ref;
  const mergedRef = useMergeRefs([refs.setReference, childRef]);

  return (
    <>
      {cloneElement(
        children,
        // Spread the child's own props FIRST, then let floating-ui compose its
        // hover/focus/aria handlers, and set the merged ref LAST so the child's
        // own `ref` can't clobber floating-ui's reference node.
        getReferenceProps({ ...childProps, ref: mergedRef }),
      )}
      {open && content ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="au-panel-in z-overlay max-w-xs rounded-md border border-slate-700 bg-slate-900/95 px-2.5 py-1.5 text-xs text-slate-100 shadow-xl shadow-black/50 backdrop-blur"
            {...getFloatingProps()}
          >
            {content}
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}
